import { Controller } from "@hotwired/stimulus";
import $ from "jquery";
import SlickGridTable from "../../../../../javascript/sgTable";
import {
  integerFormatter,
  currencyFormatter,
  currency3Formatter,
  decimal0Formatter,
  decimal1Formatter,
  decimal2Formatter,
  decimal3Formatter,
  percent1Formatter,
  percent2Formatter,
  percent3Formatter,
  dateFormatter,
  volume500kFormatter,
  isinFormatter,
} from "../../../../../javascript/utils/formatters";

// Import Sortable
import Sortable from "sortablejs";
window.Sortable = Sortable;

// Global functions for modal buttons
window.saveWatchListInlineEdit = function(event, watchListId) {
  console.log('DEBUG: Global saveWatchListInlineEdit called for watch list', watchListId);

  // Set saving flag to prevent cancel during save
  window.savingWatchList = watchListId;

  // Find the input field in the edit mode
  const editMode = document.querySelector(`.watch-list-edit-mode[data-watch-list-id="${watchListId}"]`);
  const input = editMode.querySelector('.watch-list-edit-input');
  const newName = input.value.trim();

  if (newName === '') {
    window.savingWatchList = null;
    window.cancelWatchListInlineEdit(event, watchListId);
    return;
  }

  // Ensure edit mode is visible before collecting data
  if (editMode.classList.contains('hidden')) {
    editMode.classList.remove('hidden');
  }

  // Collect filter data from the edit form
  const filterData = collectWatchListFilterData(watchListId);
  console.log('DEBUG: Collected filter data:', filterData);

  // Send update request
  fetch(`/watch_lists/${watchListId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    },
    body: JSON.stringify({
      watch_list: {
        name: newName,
        filters: filterData
      }
    })
  })
  .then(response => {
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  })
  .then(data => {
    if (data.success) {
      // Mark that changes have been made using a global flag
      window.watchListChangesMade = true;

      // Update the watch list name in display mode
      const displayMode = document.querySelector(`.watch-list-display-mode[data-watch-list-id="${watchListId}"]`);
      const nameSpan = displayMode.querySelector('.watch-list-name');
      if (nameSpan) {
        nameSpan.textContent = newName;
        nameSpan.setAttribute('data-watch-list-name', newName);
      }

      // Update the filter count display
      const filterCount = Object.keys(filterData).length;
      const filterCountElement = displayMode.querySelector('.text-xs');
      if (filterCountElement) {
        filterCountElement.textContent = `${filterCount} filter(s) applied`;
      }

      // Hide edit mode and show display mode
      window.savingWatchList = null;
      window.cancelWatchListInlineEdit(event, watchListId);

      alert('Watch list updated successfully!');
    } else {
      throw new Error(data.errors ? data.errors.join(', ') : 'Failed to update watch list');
    }
  })
  .catch(error => {
    console.error('Error updating watch list:', error);
    alert(`Error updating watch list: ${error.message}`);
    window.savingWatchList = null;
  });
};

window.cancelWatchListInlineEdit = function(event, watchListId) {
  console.log('DEBUG: Global cancelWatchListInlineEdit called for watch list', watchListId);

  // Check if we're in the middle of saving (prevent cancel during save)
  if (window.savingWatchList === watchListId) {
    console.log('DEBUG: Skipping cancel - currently saving watch list', watchListId);
    return;
  }

  // Find the display and edit mode elements
  const displayMode = document.querySelector(`.watch-list-display-mode[data-watch-list-id="${watchListId}"]`);
  const editMode = document.querySelector(`.watch-list-edit-mode[data-watch-list-id="${watchListId}"]`);
  const input = editMode.querySelector('.watch-list-edit-input');

  if (displayMode && editMode && input) {
    // Reset input value to original name
    const originalName = displayMode.querySelector('.watch-list-name').getAttribute('data-watch-list-name');
    input.value = originalName;

    // Hide edit mode and show display mode
    displayMode.classList.remove('hidden');
    editMode.classList.add('hidden');
  }
};

// Helper function to collect filter data
function collectWatchListFilterData(watchListId) {
  const editMode = document.querySelector(`.watch-list-edit-mode[data-watch-list-id="${watchListId}"]`);
  const filters = {};

  console.log('DEBUG: Collecting filter data for watch list', watchListId);
  console.log('DEBUG: Edit mode element:', editMode);

  // Collect all inputs systematically to avoid duplicates
  const allInputs = editMode.querySelectorAll('input[name*="watch_list_filters"]');
  console.log('DEBUG: Found all inputs:', allInputs.length);

  // Process each input type systematically
  allInputs.forEach(input => {
    const fullName = input.name;
    const value = input.value;

    if (!value || value.trim() === '') return;

    console.log('DEBUG: Processing input:', fullName, '=', value);

    // Extract the field name from watch_list_filters[field_name]
    const fieldName = fullName.replace('watch_list_filters[', '').replace(']', '');

    // Handle different input types
    if (fieldName.includes('_in[]')) {
      // Multi-select inputs - convert to q[field_in][] format
      const baseField = fieldName.replace('[]', '');
      const qName = `q[${baseField}][]`;

      if (!filters[qName]) {
        filters[qName] = [];
      }

      // Split comma-separated values if they exist
      if (value.includes(',')) {
        value.split(',').forEach(val => {
          const trimmedVal = val.trim();
          if (trimmedVal && !filters[qName].includes(trimmedVal)) {
            filters[qName].push(trimmedVal);
          }
        });
      } else {
        if (!filters[qName].includes(value)) {
          filters[qName].push(value);
        }
      }
    } else if (fieldName.includes('_in')) {
      // Skip - these are handled by the _in[] inputs above
      console.log('DEBUG: Skipping _in input (handled by _in[]):', fullName);
    } else {
      // Other inputs (ranges, toggles, etc.) - convert to q[field] format
      const qName = `q[${fieldName}]`;
      filters[qName] = value;
    }
  });

  console.log('DEBUG: Collected filters:', filters);
  return filters;
}

export default class extends Controller {
  static targets = [];

  static values = {
    securities: Array,
    selectedColumns: Array,
    columnDefinitions: Array,
    sortColumn: String,
    sortDirection: String,
    updateSettingsUrl: String,
    queryParamsValue: Boolean,
    userManagedWatchlistsEnabled: Boolean
  };

  // Static properties shared across all instances
  static lastAddSecuritiesCall = 0;
  static isAddingSecurities = false;

  // properties
  grid;
  isLoading = false;
  securityListChangesMade = false;
  watchListChangesMade = false;
  modalEventListeners = [];

  async connect() {
    await this.loadSettingsFromDatabase();
    this.renderTable();

    // Set initial state for add/remove selected link
    const selectedRows = JSON.parse(localStorage.getItem("selectedRows") || "[]");
    this.updateAddRemoveSelectedLinkState(selectedRows);

    // Initialize Actions Dropdown state based on current filters
    this.updateActionsDropdownButtonState(selectedRows);

    // Initial sync
    this.syncSelectedRows();
    
    // Refresh selected securities data after table is rendered
    this.refreshSelectedSecuritiesData();

    // Hide loading spinner after table renders (data is loaded)
    this.hideLoadingSpinner();

    // Initialize auto-submit for dynamic filters
    this.initializeAutoSubmit();

    // Listen for security drawer opening to re-initialize auto-submit
    const securityDrawerHandler = () => {
      // Small delay to ensure drawer content is rendered
      setTimeout(() => {
        this.initializeAutoSubmit()
      }, 500)
    }
    document.addEventListener('security-drawer-opened', securityDrawerHandler)
    this.modalEventListeners.push({ element: document, event: 'security-drawer-opened', handler: securityDrawerHandler })

    // Add event delegation for modal buttons
    const modalClickHandler = (e) => {
      if (e.target.matches('[data-modal-close]')) {
        const modalId = e.target.getAttribute('data-modal-close');
        if (modalId === 'add-to-security-list-modal') {
          this.closeAddToSecurityListModal();
        }
      }

      if (e.target.matches('[data-modal-add-securities]')) {
        const modalId = e.target.getAttribute('data-modal-add-securities');
        if (modalId === 'add-to-security-list-modal') {
          this.addSecuritiesToSecurityLists();
        }
      }
    };

    document.addEventListener('click', modalClickHandler);
    this.modalEventListeners.push({ element: document, event: 'click', handler: modalClickHandler });

    $(".slickgrid-to-csv").on("click", (e) => {
      e.preventDefault();
      this.exportSlickGridToCSV();
    })

    $("#search-toggle").on("click", () => {
      this.toggleSearch();
    })

    // Listen for selection changes to update add/remove selected link state
    const selectionChangeHandler = (event) => {
      const selectedRows = event.detail?.selectedRows || [];
      this.updateAddRemoveSelectedLinkState(selectedRows);
    };
    document.addEventListener('securities-selection-changed', selectionChangeHandler);
    this.modalEventListeners.push({ element: document, event: 'securities-selection-changed', handler: selectionChangeHandler });
  }

  disconnect() {
    // Clean up event listeners to prevent memory leaks and duplicate handlers
    this.modalEventListeners.forEach(({ element, event, handler }) => {
      element.removeEventListener(event, handler);
    });
    this.modalEventListeners = [];

    // Clean up filter event listeners
    this.removeExistingListeners()

    // Clean up debounced timeout
    if (this.debouncedFilterSubmit) {
      clearTimeout(this.debouncedFilterSubmit)
    }
  }

  async refreshSelectedSecuritiesData() {
    try {
      const selectedRowData = JSON.parse(localStorage.getItem("selectedRowData") || "[]");
      
      if (selectedRowData.length === 0) {
        console.log("No selected securities to refresh");
        return;
      }

      const ids = selectedRowData.map(row => row.id).filter(Boolean);
      
      if (ids.length === 0) {
        return;
      }

      const response = await fetch(`/securities/selected_securities?ids=${ids.join(',')}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.securities && data.securities.length > 0) {
        const securities = data.securities.map(security => ({
          ...security,
          id: security.slug
        }));
        localStorage.setItem('selectedRowData', JSON.stringify(securities));
        localStorage.setItem('selectedRows', JSON.stringify(securities.map(security => security.id)));
        
        this.updateGridWithFreshSelectedData(securities);
        
      }
    }catch (error) {
      console.error("Failed to refresh selected securities data:", error);
    }
  }

  updateGridWithFreshSelectedData(freshSecurities) {
    if (!this.grid || !this.grid.slickGridTable) {
      return;
    }

    const currentData = this.grid.slickGridTable.dataView.getItems();
    
    const updatedData = currentData.map(item => {
      const freshItem = freshSecurities.find(f => f.slug === item.slug);
      return freshItem || item;
    });
    
    this.grid.slickGridTable.dataView.beginUpdate();
    this.grid.slickGridTable.dataView.setItems(updatedData);
    this.grid.slickGridTable.dataView.endUpdate();
    
    this.restoreSelectionState();
    
  }

  restoreSelectionState() {
    if (this.grid && this.grid.slickGridTable && this.grid.slickGridTable.showCheckbox) {
      const selectedRowData = JSON.parse(localStorage.getItem("selectedRowData") || "[]");
      const selectedIds = selectedRowData.map(row => row.id);
      const selectedRowsIndex = [];
      
      this.grid.slickGridTable.dataView.getItems().forEach((item, index) => {
        if (selectedIds.includes(item.slug)) {
          selectedRowsIndex.push(index);
        }
      });
      
      this.grid.slickGridTable.grid.setSelectedRows(selectedRowsIndex);
    }
  }

  renderTable() {
    const securities = this.securitiesValue;
    const selectedColumns = this.selectedColumnsValue;
    var sortColumn = this.sortColumnValue;
    var sortDirection = this.sortDirectionValue;

    const savedSortOrder = this.getSavedSortOrder();
    let sortOrder;

    if (Array.isArray(savedSortOrder) && savedSortOrder.length > 0) {
      sortOrder = savedSortOrder;
    } else {
      let sortColumn;

      if (!this.sortColumnValue && selectedColumns.includes('issuer-name')) {
        sortColumn = 'issuer-name';
      } else if (!this.sortColumnValue) {
        sortColumn = selectedColumns[0];
      } else {
        sortColumn = this.sortColumnValue;
      }

      let sortDirection = this.sortDirectionValue;
      if (!sortDirection || (sortDirection !== 'asc' && sortDirection !== 'desc')) {
        sortDirection = 'asc';
      }

      sortOrder = [
        {
          column: sortColumn,
          direction: sortDirection
        }
      ];
    }

    // Calculate grid height with minimum 10 rows and maximum viewport space
    const rowHeight = 32; // From SlickGrid options
    const rowHeaderHeight = 30; // Approximate header height
    const pageHeaderHeight = 250; // Approximate page header height
    const selectedRows = JSON.parse(localStorage.getItem("selectedRows") || "[]");

    // Minimum height: 10 rows
    const minHeight = (10 * rowHeight) + rowHeaderHeight;

    // Maximum height: estimated remaining viewport space
    const viewportHeight = window.innerHeight;
    const maxHeight = viewportHeight - pageHeaderHeight;

    // Use the larger of min and max to ensure we see the most rows
    const calculatedHeight = Math.max(minHeight, maxHeight);

    // Convert string formatter names to actual formatter functions
    const columnDefinitions = this.columnDefinitionsValue.map(function (column) {
      // Create a copy of the column to avoid modifying the original
      const newColumn = { ...column };

      // If column id is "isin", automatically apply the ISIN formatter
      if (newColumn.id === "isin") {
        newColumn.formatter = isinFormatter;
      }
      // If formatter exists as a string, convert it to the actual function
      else if (newColumn.formatter && typeof newColumn.formatter === "string") {
        switch (newColumn.formatter) {
          case "integer":
            newColumn.formatter = integerFormatter;
            break;
          case "currency":
            newColumn.formatter = currencyFormatter;
            break;
          case "currency3":
            newColumn.formatter = currency3Formatter;
            break;
          case "decimal0":
            newColumn.formatter = decimal0Formatter;
            break;
          case "decimal1":
            newColumn.formatter = decimal1Formatter;
            break;
          case "decimal2":
            newColumn.formatter = decimal2Formatter;
            break;
          case "decimal3":
            newColumn.formatter = decimal3Formatter;
            break;
          case "percent1":
            newColumn.formatter = percent1Formatter;
            break;
          case "percent2":
            newColumn.formatter = percent2Formatter;
            break;
          case "percent3":
            newColumn.formatter = percent3Formatter;
            break;
          case "date":
            newColumn.formatter = dateFormatter;
            break;
          case "volume500k":
            newColumn.formatter = volume500kFormatter;
            break;
          // Add more cases if needed
          default:
            // If formatter string doesn't match, remove it to avoid errors
            delete newColumn.formatter;
        }
      }

      // Preserve the original CSS class and append ba-slick-cell
      newColumn.cssClass = newColumn.cssClass ? `${newColumn.cssClass} ba-slick-cell` : "ba-slick-cell";

      return newColumn;
    });

    // Columns to be displayed in the table
    const columns = [];

    // Initialize only columns with id included into selectedColumns
    let processedColumns = [];
    selectedColumns.forEach((col) => {
      const column = columnDefinitions.find((e) => e.id === col);
      if (column)
        processedColumns.push({
          ...column,
          minWidth: column.width * 1.3
        });
    });

    const savedColumnOrder = this.getSavedColumnOrder();
    if (savedColumnOrder && savedColumnOrder.length > 0) {
      const orderedColumns = [];
      savedColumnOrder.forEach(colId => {
        const col = processedColumns.find(c => c.id === colId);
        if (col) {
          orderedColumns.push(col);
        }
      });
      processedColumns.forEach(col => {
        if (!savedColumnOrder.includes(col.id)) {
          orderedColumns.push(col);
        }
      });
      columns.push(...orderedColumns);
    } else {
      columns.push(...processedColumns);
    }

    const options = {
      frozenColumn: 1, // Pin the first column to the left
      editable: false,
      enableAddRow: false,
      enableCellNavigation: true,
      asyncEditorLoading: true,
      autoEdit: false,
      enableAsyncPostRender: true,
      syncColumnCellResize: true,
      autoHeight: false, // Disable autoHeight to use calculated height
      enableAutoResize: true,
      rowHeight: rowHeight,
      enableResponsiveResize: true
    };

    // Set the grid container height
    const gridContainer = document.getElementById("security-table");
    if (gridContainer) {
      gridContainer.style.height = `${calculatedHeight}px`;
    }

    // Store securities with inventory > 0 for highlighting (keys match SecurityPresenter#to_h)
    const highlightedRows = securities
      .filter(security => security['inventory_500k_available'] && security['inventory_500k_available'] > 0)
      .map(security => security.slug);

    // Store securities with bid inventory > 0 for custom highlighting
    const bidHighlightedRows = securities
      .filter(security => security['inventory_bid_volume'] && security['inventory_bid_volume'] > 0)
      .map(security => security.slug);

    // Store securities with ask inventory > 0 for custom highlighting
    const askHighlightedRows = securities
      .filter(security => security['inventory_ask_volume'] && security['inventory_ask_volume'] > 0)
      .map(security => security.slug);

    const slickGridTable = new SlickGridTable({
      data: securities,
      columnDefinitions: columns,
      sortDefault: sortOrder,
      selectedColumns,
      selectedRows: selectedRows,
      tableTarget: "security-table",
      showCheckbox: true,
      highlightedRows,
      bidHighlightedRows,
      askHighlightedRows,
      options,
      responsiveOptions: {
        rowHeight: rowHeight,
        minVisibleRows: 10,
        rowHeaderHeight: 30,
        headerOffsetPx: 250
      }
    });

    this.grid = slickGridTable.grid;

    // Ensure grid's visual state matches localStorage state
    if (selectedRows.length === 0 && this.grid) {
      this.grid.setSelectedRows([]);
    }

    $("#security-table-search").on("input", (e) => {
      slickGridTable.onSearch(e.target.value);
    });

    slickGridTable.onSelectedRowsChanged((changedIds) => {
      // The actual selection state is managed by sgTable.js in selectedRowData
      // We'll let the selectedRowData listener handle the UI updates
    });

    slickGridTable.onSort((e, args) => this.onSort(e, args));

    this.bindGridEvents();
  }

  async loadSettingsFromDatabase() {
    try {
      const response = await fetch('/securities_display_settings', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.display_settings) {
          this.mergeDatabaseSettingsWithLocalStorage(result.display_settings);
        }
      }
    } catch (error) {
      console.error('Failed to load settings from database:', error);
    }
  }

  mergeDatabaseSettingsWithLocalStorage(dbSettings) {
    const route = window.location.pathname;
    const stored = localStorage.getItem('securityTableSettings');
    const settings = stored ? JSON.parse(stored) : {};

    if (!settings[route]) {
      settings[route] = {};
    }

    if (dbSettings.columns) {
      settings[route][`security-table_columns`] = dbSettings.columns;
    }
    if (dbSettings.columnOrder) {
      settings[route][`security-table_columnOrder`] = dbSettings.columnOrder;
    }
    if (dbSettings.sortOrder) {
      settings[route][`security-table_sortOrder`] = dbSettings.sortOrder;
    }

    localStorage.setItem('securityTableSettings', JSON.stringify(settings));
  }

  async saveSettingsToDatabase(routeSettings = null) {
    try {
      if (!routeSettings) {
        const route = window.location.pathname;
        const stored = localStorage.getItem('securityTableSettings');
        const settings = stored ? JSON.parse(stored) : {};
        routeSettings = settings[route] || {};
      }

      const displaySettings = {
        columns: routeSettings[`security-table_columns`] || this.selectedColumnsValue,
        columnOrder: routeSettings[`security-table_columnOrder`] || this.selectedColumnsValue,
        sortOrder: routeSettings[`security-table_sortOrder`] || { column: "", direction: "" }
      };

      const response = await fetch('/securities_display_settings', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        },
        body: JSON.stringify({
          display_settings: displaySettings
        })
      });

      if (response.ok) {
        console.log('Successfully saved display settings to database');
      } else {
        console.error('Failed to save display settings:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Error response:', errorText);
      }
    } catch (error) {
      console.error('Failed to save settings to database:', error);
    }
  }

  getSavedColumnOrder() {
    const route = window.location.pathname;
    const stored = localStorage.getItem('securityTableSettings');
    if (stored) {
      const settings = JSON.parse(stored);
      const routeSettings = settings[route] || {};
      return routeSettings[`security-table_columnOrder`];
    }
    return null;
  }

  getSavedSortOrder() {
    const route = window.location.pathname;
    const stored = localStorage.getItem('securityTableSettings');
    if (stored) {
      const settings = JSON.parse(stored);
      const routeSettings = settings[route] || {};
      const sort = routeSettings[`security-table_sortOrder`];
      if (Array.isArray(sort) && sort.length > 0) {
        return sort;
      }
    }
    return [];
  }

  saveTableSettings(key, value) {
    const route = window.location.pathname;
    const stored = localStorage.getItem('securityTableSettings');
    const settings = stored ? JSON.parse(stored) : {};

    if (!settings[route]) {
      settings[route] = {};
    }

    if (value === null) {
      delete settings[route][key];
    } else {
      settings[route][key] = value;
    }

    localStorage.setItem('securityTableSettings', JSON.stringify(settings));
    this.saveSettingsToDatabase(settings[route]);
  }

  onColumnReorder(event, args) {
    const { impactedColumns: columns } = args;
    const columnOrder = columns.map(col => col.id);
    this.saveTableSettings(`security-table_columnOrder`, columnOrder);
  }

  onSort(event, args) {
    const sortOrder = args.sortCols.map(c => ({
      column: c.sortCol.field,
      direction: c.sortAsc ? "asc" : "desc"
    }));
    this.saveTableSettings(`security-table_sortOrder`, sortOrder);
  }

  bindGridEvents() {
    if (!this.grid) return;

    if (this._sortBoundGrid !== this.grid && this.grid.onSort) {
      this.grid.onSort.subscribe((e, args) => this.onSort(e, args));
      this._sortBoundGrid = this.grid;
    }

    if (this._reorderBoundGrid !== this.grid && this.grid.onColumnsReordered) {
      this.grid.onColumnsReordered.subscribe((e, args) => this.onColumnReorder(e, args));
      this._reorderBoundGrid = this.grid;
    }
  }

  handleSelectedDataChange() {
    try {
      const selectedIds = JSON.parse(localStorage.getItem('selectedRowData') || '[]').map(item => item.id);

      // Update selectedRows to keep them in sync
      localStorage.setItem('selectedRows', JSON.stringify(selectedIds));

      // Update the add/remove selected link state
      this.updateAddRemoveSelectedLinkState(selectedIds);

      // Dispatch custom event for selection changes
      document.dispatchEvent(new CustomEvent('securities-selection-changed', {
        detail: { selectedRows: selectedIds }
      }));
    } catch (e) {
      console.error('Error handling selected data change:', e);
    }
  }

  syncSelectedRows() {
    try {
      const selectedIds = JSON.parse(localStorage.getItem('selectedRowData') || '[]').map(item => item.id);
      localStorage.setItem('selectedRows', JSON.stringify(selectedIds));
      return selectedIds;
    } catch (e) {
      console.error('Error syncing selectedRows:', e);
      return [];
    }
  }

  updateAddRemoveSelectedLinkState(selectedRows) {
    const addRemoveLink = document.querySelector('[data-action*="openAddToSecurityListModal"], [data-action*="removeSecuritiesFromSecurityList"]');
    if (!addRemoveLink) return;

    const hasSelections = selectedRows && selectedRows.length > 0;

    if (hasSelections) {
      // Enable the link
      addRemoveLink.classList.remove('opacity-50', 'cursor-not-allowed', 'pointer-events-none');
      addRemoveLink.classList.add('cursor-pointer', 'hover:bg-neutral-100', 'dark:hover:bg-neutral-600', 'dark:hover:text-white');
    } else {
      // Disable the link
      addRemoveLink.classList.add('opacity-50', 'cursor-not-allowed', 'pointer-events-none');
      addRemoveLink.classList.remove('cursor-pointer', 'hover:bg-neutral-100', 'dark:hover:bg-neutral-600', 'dark:hover:text-white');
    }

    // Update Actions dropdown button state
    this.updateActionsDropdownButtonState(selectedRows);
  }

  updateActionsDropdownButtonState(selectedRows) {
    const actionsButton = document.getElementById('actionsDropdownButton');
    if (!actionsButton) return;

    const hasSelections = selectedRows && selectedRows.length > 0;
    const hasQueryFilters = this.hasQueryParameterFilters();
    const watchlistsEnabled = this.userManagedWatchlistsEnabledValue === true;
    // When watchlist feature is disabled, only enable on row selections (not on filters alone)
    const shouldEnable = hasSelections || (hasQueryFilters && watchlistsEnabled);
    const iconElement = actionsButton.querySelector('i');

    if (shouldEnable) {
      // Enable the button
      actionsButton.disabled = false;
      actionsButton.classList.remove('opacity-50', 'cursor-not-allowed');
      actionsButton.classList.add('cursor-pointer');

      // Update icon based on state
      if (iconElement) {
        // Remove all possible icon classes first
        iconElement.classList.remove('fa-regular', 'fa-solid', 'fa-square-check', 'fa-bars');
        if (hasSelections) {
          // Show solid checkmark when selections are present
          iconElement.classList.add('fa-solid', 'fa-square-check');
        } else if (hasQueryFilters) {
          // Show bars icon when only filters are present
          iconElement.classList.add('fa-solid', 'fa-bars');
        }
      }

      // Update dropdown options visibility
      this.updateDropdownOptionsVisibility(hasSelections, hasQueryFilters);

      // Re-enable popover functionality
      if (!actionsButton.hasAttribute('data-popover-target')) {
        actionsButton.setAttribute('data-popover-target', 'actions-dropdown');
        // Re-initialize the popover for this button
        if (window.initPopovers) {
          window.initPopovers();
        }
      }
    } else {
      // Disable the button
      actionsButton.disabled = true;
      actionsButton.classList.add('opacity-50', 'cursor-not-allowed');
      actionsButton.classList.remove('cursor-pointer');

      // Change icon back to regular checkmark
      if (iconElement) {
        iconElement.classList.remove('fa-solid', 'fa-square-check', 'fa-bars');
        iconElement.classList.add('fa-regular', 'fa-square-check');
      }

      // Completely disable popover functionality by removing the attribute
      actionsButton.removeAttribute('data-popover-target');

      // Hide any open popover
      const popover = document.getElementById('actions-dropdown');
      if (popover) {
        popover.classList.add('opacity-0', 'invisible');
      }

      // Remove any existing click event listeners and add a blocking one
      const newButton = actionsButton.cloneNode(true);
      actionsButton.parentNode.replaceChild(newButton, actionsButton);

      // The new button will not have the data-popover-target attribute, so it won't trigger popovers
    }
  }

  updateDropdownOptionsVisibility(hasSelections, hasQueryFilters) {
    // Options that should be hidden when only filters are set (no selections)
    const optionsToHide = [
      '.create-quote-option',
      '.create-security-list-option', 
      '.add-to-security-list-option',
      '.remove-from-security-list-option'
    ];

    optionsToHide.forEach(selector => {
      const element = document.querySelector(selector);
      if (element) {
        if (hasQueryFilters && !hasSelections) {
          // Hide options when only filters are set and no selections
          element.style.display = 'none';
        } else {
          // Show options when selections are present
          element.style.display = '';
        }
      }
    });
  }

  // Method to manually update Actions Dropdown state (for debugging)
  updateActionsDropdownState() {
    const selectedRows = JSON.parse(localStorage.getItem("selectedRows") || "[]");
    this.updateActionsDropdownButtonState(selectedRows);
  }

  // Check if query parameter filters are present
  hasQueryParameterFilters() {
    // Check URL parameters for query filters
    const urlParams = new URLSearchParams(window.location.search);

    // Check for any query parameters that start with 'q[' (Rails nested parameters)
    for (const [key, value] of urlParams.entries()) {
      if (key.startsWith('q[') && value && value.trim() !== '') {
        return true;
      }
    }

    // Also check if the data attribute is available as fallback
    return this.queryParamsValue === true;
  }

  submitForm() {
    // Show loading spinner when submitting form
    this.showLoadingSpinner();

    const form = document.getElementById("securities-form");

    // Disable inputs with empty values
    form.querySelectorAll('input[name]').forEach(input => {
      const value = input.value;
      if (value === "" || value === null) {
        input.disabled = true;
      }
    });

    const formData = new FormData(form)
    const params = new URLSearchParams()

    // Only include enabled inputs in the URL parameters
    for (const [key, value] of formData.entries()) {
      // Find the corresponding input element
      const input = form.querySelector(`input[name="${key}"]`)
      // Only include if the input is enabled (not disabled)
      if (!input || !input.disabled) {
        params.append(key, value)
      }
    }

    const queryString = params.toString()
    if (queryString !== "") {
      history.replaceState({}, "", `${window.location.pathname}?${queryString}`)
    } else {
      // If no parameters, remove all query parameters from URL
      history.replaceState({}, "", window.location.pathname)
    }
    form.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }))
  }

  handleDynamicFilterSubmit() {
    // Get the dynamic filters container
    const dynamicFiltersContainer = document.querySelector('[data-filter-mapping]')
    if (!dynamicFiltersContainer) {
      console.warn('Dynamic filters container not found')
      return
    }

    // Read configuration from data attributes
    const FILTER_MAPPING = JSON.parse(dynamicFiltersContainer.dataset.filterMapping || '{}')
    const AUTO_ELEMENTS = Object.keys(FILTER_MAPPING)

    const form = document.getElementById("securities-form")

    // Clear any existing dynamic filter inputs to avoid duplicates
    // This includes all filter inputs that might have been added by previous submissions
    const existingDynamicInputs = form.querySelectorAll('input[name^="q["][name*="_eq"], input[name^="q["][name*="_gteq"], input[name^="q["][name*="_lteq"], input[name^="q["][name*="_in"]')
    existingDynamicInputs.forEach(input => input.remove())

    // Iterate through AUTO_ELEMENTS and collect their values based on filter type
    AUTO_ELEMENTS.forEach(field => {
      const filterConfig = FILTER_MAPPING[field]
      const filterField = filterConfig?.field
      const multiplier = filterConfig?.multiplier
      const filterType = filterConfig?.filter_type

      if (filterField) {
        switch (filterType) {
          case 'Decimal Range':
          case 'Date Range':
            // Handle range inputs (min/max)
            const minInput = document.getElementById(`min-${field}`)
            const maxInput = document.getElementById(`max-${field}`)

            // Add min value if present
            if (minInput && minInput.value.trim() !== '') {
              const minHiddenInput = document.createElement("input")
              minHiddenInput.type = "hidden"
              minHiddenInput.name = `q[${filterField}_gteq]`
              minHiddenInput.value = multiplier ? minInput.value * multiplier : minInput.value
              form.appendChild(minHiddenInput)
            }

            // Add max value if present
            if (maxInput && maxInput.value.trim() !== '') {
              const maxHiddenInput = document.createElement("input")
              maxHiddenInput.type = "hidden"
              maxHiddenInput.name = `q[${filterField}_lteq]`
              maxHiddenInput.value = multiplier ? maxInput.value * multiplier : maxInput.value
              form.appendChild(maxHiddenInput)
            }
            break

          case 'Multi Select':
            // Handle multi-select dropdown values - collect from ALL hidden inputs with the same name
            const multiSelectInputs = document.querySelectorAll(`input[name="q[${filterField}_in][]"]`)

            if (multiSelectInputs.length > 0) {
              // Collect all non-empty values from all inputs
              const values = []
              multiSelectInputs.forEach(input => {
                if (input.value && input.value.trim() !== '') {
                  values.push(input.value.trim())
                }
              })

              if (values.length > 0) {
                values.forEach(value => {
                  const hiddenInput = document.createElement("input")
                  hiddenInput.type = "hidden"
                  hiddenInput.name = `q[${filterField}_in][]`
                  hiddenInput.value = value
                  form.appendChild(hiddenInput)
                })
              }
            }
            break

          case 'Toggle Switch':
            // Handle toggle switch values - only include if checked
            const toggleSwitch = document.querySelector(`input[id="${field}"]`)
            if (toggleSwitch) {
              if (toggleSwitch.checked) {
                const hiddenInput = document.createElement("input")
                hiddenInput.type = "hidden"
                hiddenInput.name = `q[${filterField}_eq]`
                hiddenInput.value = '1'
                form.appendChild(hiddenInput)
              }
            }
            break

          default:
            console.warn(`Unknown filter type: ${filterType} for field: ${field}`)
        }
      }
    })

    this.submitForm()
  }

  // Debounced auto-submit functionality
  debouncedFilterSubmit = null
  filterEventListeners = new Map() // Track event listeners to prevent duplicates
  isSubmitting = false // Flag to prevent multiple simultaneous submissions

  handleFilterChange() {
    // Prevent multiple submissions if one is already in progress
    if (this.isSubmitting) {
      return
    }

    // Clear existing timeout
    if (this.debouncedFilterSubmit) {
      clearTimeout(this.debouncedFilterSubmit)
    }

    // Set new timeout for 500ms
    this.debouncedFilterSubmit = setTimeout(() => {
      this.isSubmitting = true
      this.handleDynamicFilterSubmit()
      // Reset flag after submission
      setTimeout(() => {
        this.isSubmitting = false
      }, 100) // Give 100ms buffer after submission
    }, 500)
  }

  handleToggleSwitchChange(event) {
    const toggleSwitch = event.target
    const toggleId = toggleSwitch.id

    // Find the hidden input that corresponds to this toggle switch
    // by looking for one with the same field name as the toggle ID
    const fieldMap = {
      'green-bond': 'green_bond',
      'sustainability-linked-bond': 'sustainability_linked_bond',
      'social-benefit-bond': 'social_benefit_bond'
    }

    const field = fieldMap[toggleId]

    if (!field) {
      return
    }

    // Update the hidden input value based on checkbox state
    const targetHiddenInput = document.querySelector(`input[name="q[${field}_eq]"]`)
    if (targetHiddenInput) {
      targetHiddenInput.value = toggleSwitch.checked ? '1' : '0'
    }

    // Use the same debounced submission as other filter types
    this.handleFilterChange()
  }

  removeExistingListeners() {
    // Remove all existing filter event listeners
    this.filterEventListeners.forEach((listener, element) => {
      element.removeEventListener('input', listener)
      element.removeEventListener('change', listener)
    })
    this.filterEventListeners.clear()
  }

  initializeAutoSubmit() {
    // Remove any existing listeners first to prevent duplicates
    this.removeExistingListeners()
    // Get the dynamic filters container
    const dynamicFiltersContainer = document.querySelector('[data-filter-mapping]')
    if (!dynamicFiltersContainer) {
      console.warn('Dynamic filters container not found')
      return
    }

    // Read configuration from data attributes
    const FILTER_MAPPING = JSON.parse(dynamicFiltersContainer.dataset.filterMapping || '{}')
    const AUTO_ELEMENTS = Object.keys(FILTER_MAPPING)

    // Add event listeners to all filter inputs
    AUTO_ELEMENTS.forEach(field => {
      const filterConfig = FILTER_MAPPING[field]
      const filterType = filterConfig?.filter_type

      switch (filterType) {
        case 'Decimal Range':
        case 'Date Range':
          // Add listeners to min and max inputs
          const minInput = document.getElementById(`min-${field}`)
          const maxInput = document.getElementById(`max-${field}`)

          if (minInput) {
            const minListener = () => this.handleFilterChange()
            minInput.addEventListener('input', minListener)
            this.filterEventListeners.set(minInput, minListener)
          }
          if (maxInput) {
            const maxListener = () => this.handleFilterChange()
            maxInput.addEventListener('input', maxListener)
            this.filterEventListeners.set(maxInput, maxListener)
          }
          break

        case 'Multi Select':
          // Add listener to the hidden input of the multi-select component
          const multiSelectInput = document.querySelector(`input[name="q[${filterConfig.field}_in][]"]`)
          if (multiSelectInput) {
            const multiSelectListener = () => this.handleFilterChange()
            multiSelectInput.addEventListener('change', multiSelectListener)
            this.filterEventListeners.set(multiSelectInput, multiSelectListener)
          }
          break

        case 'Toggle Switch':
          // Toggle switches use data-action, no manual listeners needed
          break

        default:
          console.warn(`Unknown filter type: ${filterType} for field: ${field}`)
      }
    })
  }

  handleDynamicFilterReset() {
    // Clear all other input fields in the drawer
    const drawer = document.getElementById('security-drawer');
    if (drawer) {
      drawer.querySelectorAll('input, select, textarea').forEach(input => {
        if (input.type === 'checkbox' || input.type === 'radio') {
          input.checked = false;
        } else {
          input.value = '';
        }
      });

      // Clear multi-select visual displays
      const multiSelectComponents = drawer.querySelectorAll('[data-controller="uikit--select-multi"]');
      multiSelectComponents.forEach(component => {
        try {
          const controller = this.application.getControllerForElementAndIdentifier(
            component, 'uikit--select-multi'
          );
          if (controller && typeof controller.clear === 'function') {
            controller.clear();
          } else {
            // Fallback: manually clear the visual display
            this.clearMultiSelectDisplay(component);
          }
        } catch (error) {
          console.warn('Failed to clear multi-select via controller:', error);
          // Fallback: manually clear the visual display
          this.clearMultiSelectDisplay(component);
        }
      });
    }

    // Clear URL parameters to remove all query params
    history.replaceState({}, "", window.location.pathname);

    this.submitForm()
  }

  exportSlickGridToCSV() {
    const securities = this.securitiesValue;

    // Create CSV content using securities data and selectedColumns
    const csvContent = this.convertRowsToCsv(securities);

    // Create and download CSV
    const securityCSV = new Blob([csvContent], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(securityCSV);
    link.target = '_blank';
    link.download = "securities.csv";

    link.click();
  }

  convertRowsToCsv(rows) {
    var colDelim = ','
    var rowDelim = '\r\n'
    const visibleColumns = this.grid.getColumns().slice(1)
    const header = visibleColumns.map(column => column.name).join(colDelim) + rowDelim

    const data = rows.map(row => visibleColumns.map(column => (`"${row[column.field.split("-").join("_")] || ''}"`)).join(','))
    return `${header}${data.join('\n')}`
  }

  openTableSettingsModal(event) {
    event.preventDefault();

    const modalInstance = document.getElementById("security-adjust-settings-modal")
    modalInstance.classList.remove("hidden")
    modalInstance.setAttribute("aria-hidden", "false")
  }

  closeTableSettingsModal() {
    const modalInstance = document.getElementById("security-adjust-settings-modal")
    modalInstance.classList.add("hidden")
    modalInstance.setAttribute("aria-hidden", "true")
  }

  submitAdjustSettingsForm(event) {
    event.preventDefault();

    const form = document.getElementById('securities-adjust-settings-form');
    if (!form) return;

    // requestSubmit() fires the submit event so Turbo can intercept; form.submit() does not
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.submit();
    }
  }

  toggleSearch() {
    const searchInput = document.getElementById("security-table-search")
    const clearButton = document.getElementById("security-table-search-clear")

    if (searchInput) {
      if (searchInput.classList.contains('w-0')) {
        searchInput.classList.remove('w-0', 'opacity-0', 'pointer-events-none')
        searchInput.classList.add('w-64', 'opacity-100')
        searchInput.focus()

        // Show clear button when search is expanded
        if (clearButton) {
          clearButton.classList.remove('w-0', 'opacity-0', 'pointer-events-none')
          clearButton.classList.add('opacity-100')
        }
      } else {
        searchInput.classList.remove('w-64', 'opacity-100')
        searchInput.classList.add('w-0', 'opacity-0', 'pointer-events-none')

        // Hide clear button when search is collapsed
        if (clearButton) {
          clearButton.classList.remove('opacity-100')
          clearButton.classList.add('w-0', 'opacity-0', 'pointer-events-none')
        }
      }
    }
  }

  clearSearch() {
    const searchInput = document.getElementById("security-table-search")

    if (searchInput) {
      // Clear the input value
      searchInput.value = ''

      // Trigger the input event to clear the search filter
      const event = new Event('input', { bubbles: true })
      searchInput.dispatchEvent(event)

      // Collapse the search input
      this.toggleSearch()
    }
  }

  showLoadingSpinner() {
    this.isLoading = true;
    const spinnerButton = document.querySelector('[data-spinner-button]');
    const searchButton = document.querySelector('[data-search-button]');
    if (spinnerButton) {
      spinnerButton.classList.remove('hidden');
    }
    if (searchButton) {
      searchButton.classList.add('hidden');
    }
    this.disableButtons();

    const resetButton = document.getElementById('reset-filters-button');
    const loadingButton = document.getElementById('reset-filters-spinner');

    if (resetButton && loadingButton) {
      resetButton.classList.add('!hidden');
      loadingButton.classList.remove('!hidden');
    }
  }

  hideLoadingSpinner() {
    this.isLoading = false;
    const spinnerButton = document.querySelector('[data-spinner-button]');
    const searchButton = document.querySelector('[data-search-button]');
    if (spinnerButton) {
      spinnerButton.classList.add('hidden');
    }
    if (searchButton) {
      searchButton.classList.remove('hidden');
    }
    this.enableButtons();

    const resetButton = document.getElementById('reset-filters-button');
    const loadingButton = document.getElementById('reset-filters-spinner');

    if (resetButton && loadingButton) {
      resetButton.classList.remove('!hidden');
      loadingButton.classList.add('!hidden');
    }
  }

  disableButtons() {
    const buttons = document.querySelectorAll('[data-disable-during-loading]');
    buttons.forEach(button => {
      button.disabled = true;
    });
  }

  enableButtons() {
    const buttons = document.querySelectorAll('[data-disable-during-loading]');
    buttons.forEach(button => {
      button.disabled = false;
    });
  }

  // --- Security List Management Modal ---
  openSecurityListManagementModal(event) {
    event.preventDefault();

    const modal = document.getElementById("security-list-management-modal");
    if (modal) {
      modal.classList.remove("hidden");
      modal.setAttribute("aria-hidden", "false");

      // Initialize drag and drop functionality
      this.initializeDragAndDrop();
    }
  }

  closeSecurityListManagementModal() {
    const modal = document.getElementById("security-list-management-modal");
    if (modal) {
      modal.classList.add("hidden");
      modal.setAttribute("aria-hidden", "true");

      // Clean up drag and drop functionality
      this.cleanupDragAndDrop();

      // Check for changes using modal data attribute as backup
      const modalChangesMade = modal.dataset.changesMade === 'true';
      const shouldRefresh = this.securityListChangesMade || modalChangesMade;

      // Refresh page if changes were made
      if (shouldRefresh) {
        window.location.reload();
      }
    }
  }

  cleanupDragAndDrop() {
    // Clean up Sortable.js instance
    const container = document.getElementById('security-lists-list');
    if (container && container._sortable) {
      container._sortable.destroy();
      delete container._sortable;
    }
  }

  startInlineEdit(event) {
    event.preventDefault();

    const securityListId = event.currentTarget.getAttribute('data-security-list-id');
    const securityListName = event.currentTarget.getAttribute('data-security-list-name');

    // Find the display and edit mode elements
    const displayMode = document.querySelector(`.security-list-display-mode[data-security-list-id="${securityListId}"]`);
    const editMode = document.querySelector(`.security-list-edit-mode[data-security-list-id="${securityListId}"]`);
    const input = editMode.querySelector('.security-list-edit-input');

    if (displayMode && editMode && input) {
      // Hide display mode and show edit mode
      displayMode.classList.add('hidden');
      editMode.classList.remove('hidden');

      // Focus and select text
      input.focus();
      input.select();

      // Add event listeners
      input.addEventListener('blur', (e) => this.saveInlineEdit(e));
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          this.saveInlineEdit(e);
        } else if (e.key === 'Escape') {
          this.cancelInlineEdit(e);
        }
      });
    }
  }

  // --- Watch List Management ---
  saveWatchList(event) {
      event.preventDefault();

      const watchListName = document.getElementById('watch-list-name').value.trim();
      if (!watchListName) {
        alert('Please enter a watch list name');
        return;
      }

      // Get current filter parameters
      const currentFilters = this.getCurrentFilterParameters();

      // Get CSRF token
      const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

      // Send JSON request to create watch list
      fetch('/watch_lists', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          watch_list: {
            name: watchListName,
            filters: currentFilters
          }
        })
      })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          // Clear the input field
          document.getElementById('watch-list-name').value = '';

          // Show success message
          alert('Watch list saved successfully!');

          // Reload the page to show the new watch list
          window.location.reload();
        } else {
          alert(`Error creating watch list: ${data.errors}`);
        }
      })
      .catch(error => {
        console.error('Error creating watch list:', error);
        alert('Error creating watch list. Please try again.');
      });
    }

  getCurrentFilterParameters() {
    const filters = {};

    // First, collect URL parameters as they contain the complete state
    const urlParams = new URLSearchParams(window.location.search);
    console.log('DEBUG: URL parameters:', Array.from(urlParams.entries()));

    for (const [key, value] of urlParams.entries()) {
      if (value && value.trim() !== '') {
        if (filters[key]) {
          // Handle multiple values for the same key
          if (Array.isArray(filters[key])) {
            if (!filters[key].includes(value)) {
              filters[key].push(value);
            }
          } else {
            if (filters[key] !== value) {
              filters[key] = [filters[key], value];
            }
          }
        } else {
          filters[key] = value;
        }
      }
    }

    // Then collect form data as fallback for non-URL parameters
    const form = document.getElementById("securities-form");
    if (form) {
      const allInputs = form.querySelectorAll('input, select, textarea');
      console.log('DEBUG: Found', allInputs.length, 'form inputs');

      allInputs.forEach(input => {
        if (input.type === 'hidden' || input.type === 'text' || input.type === 'number' || input.type === 'date' || input.type === 'checkbox' || input.type === 'radio' || input.tagName === 'SELECT') {
          const name = input.name;
          const value = input.value;

          if (name && value && value.trim() !== '') {
            // Skip disabled inputs and unchecked checkboxes/radios
            if (input.disabled || (input.type === 'checkbox' && !input.checked) || (input.type === 'radio' && !input.checked)) {
              return;
            }

            // Skip if this is a multiselect field that we already have from URL
            if (name.includes('_in[]') || name.includes('_in')) {
              // Check if we already have this field from URL parameters
              const baseName = name.replace('[]', '');
              if (filters[baseName] || filters[name]) {
                console.log('DEBUG: Skipping form input (already have from URL):', name, '=', value);
                return;
              }
            }

            // Only add form data if not already present from URL
            if (!filters[name]) {
              console.log('DEBUG: Adding form input:', name, '=', value);
              filters[name] = value;
            }
          }
        }
      });
    }

    // Clean up multi-select fields to avoid duplicates and ensure proper array format
    Object.keys(filters).forEach(key => {
      if (key.includes('_in[]') || key.includes('_in')) {
        if (!Array.isArray(filters[key])) {
          filters[key] = [filters[key]];
        }

        // Remove duplicates and clean up comma-separated values
        const values = filters[key];
        const cleanedValues = [];

        values.forEach(val => {
          if (val.includes(',')) {
            // Split comma-separated values and add individually
            val.split(',').forEach(splitVal => {
              const trimmed = splitVal.trim();
              if (trimmed && !cleanedValues.includes(trimmed)) {
                cleanedValues.push(trimmed);
              }
            });
          } else {
            const trimmed = val.trim();
            if (trimmed && !cleanedValues.includes(trimmed)) {
              cleanedValues.push(trimmed);
            }
          }
        });

        filters[key] = cleanedValues;

        // Remove the [] version if we have both versions
        const baseKey = key.replace('[]', '');
        if (key.includes('[]') && filters[baseKey]) {
          delete filters[baseKey];
        }
      }
    });

    console.log('DEBUG: Collected filters for watch list creation:', filters);
    return filters;
  }

    loadWatchLists() {
      // This would typically fetch from the server, but for now we'll use the data from the page
      const watchListsContainer = document.getElementById('watch-lists-container');
      if (!watchListsContainer) return;

      // Get watch lists from the modal data (passed from server)
      const watchListModal = document.getElementById('watch-list-management-modal');
      if (watchListModal && watchListModal.dataset.watchLists) {
        const watchLists = JSON.parse(watchListModal.dataset.watchLists);
        this.renderWatchLists(watchLists);
      }
    }

    renderWatchLists(watchLists) {
      const container = document.getElementById('watch-lists-container');
      if (!container) return;

      if (watchLists.length === 0) {
        container.innerHTML = '<div class="text-center py-4 text-neutral-500">No watch lists yet</div>';
        return;
      }

      const html = watchLists.map(watchList => `
        <div class="flex items-center justify-between p-3 border border-neutral-200 rounded-lg dark:border-neutral-700">
          <div class="flex-1">
            <div class="font-medium text-neutral-900 dark:text-white">${watchList.name}</div>
            <div class="text-sm text-neutral-500">${Object.keys(watchList.filters).length} filter(s)</div>
          </div>
          <div class="flex items-center space-x-2">
            <button
              type="button"
              class="p-1 text-neutral-400 hover:text-neutral-600 dark:text-neutral-500 dark:hover:text-neutral-300"
              onclick="this.applyWatchList(${watchList.id})"
              title="Apply watch list"
            >
              <i class="fa-solid fa-play text-sm"></i>
            </button>
            <button
              type="button"
              class="p-1 text-neutral-400 hover:text-red-600 dark:text-neutral-500 dark:hover:text-red-400"
              onclick="this.deleteWatchList(${watchList.id})"
              title="Delete watch list"
            >
              <i class="fa-solid fa-trash text-sm"></i>
            </button>
          </div>
        </div>
      `).join('');

      container.innerHTML = html;
    }

    applyWatchList(watchListId) {
      // This would apply the watch list filters to the current view
      // For now, we'll redirect to the securities page with the watch list parameter
      window.location.href = `/securities?watch_list=${watchListId}`;
    }

    deleteWatchList(watchListId) {
      if (!confirm('Are you sure you want to delete this watch list?')) {
        return;
      }

      const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

      fetch(`/watch_lists/${watchListId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        }
      })
      .then(response => {
        if (response.ok) {
          this.loadWatchLists();
          alert('Watch list deleted successfully!');
        } else {
          throw new Error('Failed to delete watch list');
        }
      })
      .catch(error => {
        console.error('Error deleting watch list:', error);
        alert('Error deleting watch list. Please try again.');
      });
    }

    // --- Watch List Modal Management ---
    openWatchListManagementModal(event) {
      event.preventDefault();

      // Check if user managed watch lists are enabled
      if (!this.userManagedWatchlistsEnabledValue) {
        alert("Watch list feature is not available.");
        return;
      }

      const modal = document.getElementById("watch-list-management-modal");
      if (modal) {
        modal.classList.remove("hidden");
        modal.setAttribute("aria-hidden", "false");

        // Reset change tracking variables for clean state
        this.watchListChangesMade = false;
        modal.dataset.changesMade = 'false';
        window.watchListChangesMade = false;

        // Initialize drag and drop functionality
        this.initializeWatchListDragAndDrop();
      }
    }

    closeWatchListManagementModal() {
      const modal = document.getElementById("watch-list-management-modal");
      if (modal) {
        modal.classList.add("hidden");
        modal.setAttribute("aria-hidden", "true");

        // Clean up drag and drop functionality
        this.cleanupWatchListDragAndDrop();

        // Check for changes using modal data attribute as backup
        const modalChangesMade = modal.dataset.changesMade === 'true';
        const globalChangesMade = window.watchListChangesMade === true;
        const shouldRefresh = this.watchListChangesMade || modalChangesMade || globalChangesMade;

        // Refresh page if changes were made
        if (shouldRefresh) {
          window.location.reload();
        }
      }
    }

    initializeWatchListDragAndDrop() {
      const container = document.getElementById('watch-lists-list');
      if (!container) return;

      // Clean up any existing Sortable instance
      if (container._sortable) {
        container._sortable.destroy();
      }

      // Initialize Sortable.js
      container._sortable = new Sortable(container, {
        animation: 150,
        handle: '[data-sortable-handle]',
        ghostClass: 'sortable-ghost',
        chosenClass: 'sortable-chosen',
        dragClass: 'sortable-drag',
        onEnd: (evt) => {
          const oldIndex = evt.oldIndex;
          const newIndex = evt.newIndex;

          if (oldIndex !== newIndex) {
            this.reorderWatchLists(oldIndex, newIndex);
          }
        }
      });
    }

    cleanupWatchListDragAndDrop() {
      const container = document.getElementById('watch-lists-list');
      if (container && container._sortable) {
        container._sortable.destroy();
        delete container._sortable;
      }
    }

    reorderWatchLists(oldIndex, newIndex) {
      const container = document.querySelector('#watch-lists-list');
      if (!container) return;

      // Update priorities for all items
      const newPositions = Array.from(container.children).map((item, index) => ({
        id: item.getAttribute('data-watch-list-id'),
        priority: index + 1
      }));

      this.updateWatchListPositions(newPositions);
    }

    updateWatchListPositions(positions) {
      fetch('/watch_lists/reorder', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        },
        body: JSON.stringify({ positions })
      })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        if (data.success) {
          // Mark that changes have been made
          this.watchListChangesMade = true;

          // Also set modal data attribute and global variable for backup
          const modal = document.getElementById("watch-list-management-modal");
          if (modal) {
            modal.dataset.changesMade = 'true';
          }
          window.watchListChangesMade = true;

          console.log('Watch list order updated successfully');
        } else {
          throw new Error(data.errors ? data.errors.join(', ') : 'Failed to update watch list positions');
        }
      })
      .catch(error => {
        alert(`Error updating watch list positions: ${error.message}`);
      });
    }

    // --- Watch List Inline Editing ---
    startWatchListInlineEdit(event) {
      event.preventDefault();

      const watchListId = event.currentTarget.getAttribute('data-watch-list-id');
      const watchListName = event.currentTarget.getAttribute('data-watch-list-name');

      // Find the display and edit mode elements
      const displayMode = document.querySelector(`.watch-list-display-mode[data-watch-list-id="${watchListId}"]`);
      const editMode = document.querySelector(`.watch-list-edit-mode[data-watch-list-id="${watchListId}"]`);
      const input = editMode.querySelector('.watch-list-edit-input');

      if (displayMode && editMode && input) {
        // Hide display mode and show edit mode
        displayMode.classList.add('hidden');
        editMode.classList.remove('hidden');

        // Focus and select text
        input.focus();
        input.select();

        // Reinitialize Uikit components in edit mode
        this.reinitializeUikitComponents(editMode);

        // Add event listeners
        input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            this.saveWatchListInlineEdit(e);
          } else if (e.key === 'Escape') {
            this.cancelWatchListInlineEdit(e);
          }
        });
      }
    }

    saveWatchListInlineEdit(event) {
      try {
        console.log('DEBUG: saveWatchListInlineEdit called');
        console.log('DEBUG: Event target:', event.target);

      const input = event.target;
      const watchListId = input.getAttribute('data-watch-list-id');
      const newName = input.value.trim();

      console.log('DEBUG: WatchListId:', watchListId);
      console.log('DEBUG: New name:', newName);

      if (newName === '') {
        this.cancelWatchListInlineEdit(event);
        return;
      }

      // Collect filter data from the edit form
      console.log('DEBUG: About to collect filter data...');
      const filterData = this.collectWatchListFilterData(watchListId);

      // Send update request
      fetch(`/watch_lists/${watchListId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        },
        body: JSON.stringify({
          watch_list: {
            name: newName,
            filters: filterData
          }
        })
      })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        if (data.success) {
          // Mark that changes have been made
          this.watchListChangesMade = true;

          // Update the watch list name in display mode
          const displayMode = document.querySelector(`.watch-list-display-mode[data-watch-list-id="${watchListId}"]`);
          const nameSpan = displayMode.querySelector('.watch-list-name');
          if (nameSpan) {
            nameSpan.textContent = newName;
            nameSpan.setAttribute('data-watch-list-name', newName);
          }

          // Update the filter count display
          const filterCount = Object.keys(filterData).length;
          const filterCountElement = displayMode.querySelector('.text-xs');
          if (filterCountElement) {
            filterCountElement.textContent = `${filterCount} filter(s) applied`;
          }

          // Update the edit button's data attribute
          const editButton = document.querySelector(`[data-action*="startWatchListInlineEdit"][data-watch-list-id="${watchListId}"]`);
          if (editButton) {
            editButton.setAttribute('data-watch-list-name', newName);
          }

          // Hide edit mode and show display mode
          this.cancelWatchListInlineEdit(event);
        } else {
          throw new Error(data.errors ? data.errors.join(', ') : 'Failed to update watch list');
        }
      })
      .catch(error => {
        alert(`Error updating watch list: ${error.message}`);
        this.cancelWatchListInlineEdit(event);
      });
      } catch (error) {
        console.error('DEBUG: Error in saveWatchListInlineEdit:', error);
        alert(`Error: ${error.message}`);
      }
    }

    collectWatchListFilterData(watchListId) {
      const editMode = document.querySelector(`.watch-list-edit-mode[data-watch-list-id="${watchListId}"]`);
      const filters = {};

      console.log('DEBUG: Collecting filter data for watch list', watchListId);
      console.log('DEBUG: Edit mode element:', editMode);

      // Collect multi-select values from Uikit SelectMulti components
      // First, collect from hidden inputs (which have the actual values)
      const allHiddenInputs = editMode.querySelectorAll('input[type="hidden"]');
      console.log('DEBUG: All hidden inputs:', allHiddenInputs.length);
      allHiddenInputs.forEach(input => {
        console.log('DEBUG: Hidden input name:', input.name);
      });

      const hiddenMultiSelectInputs = editMode.querySelectorAll('input[type="hidden"][name*="watch_list_filters"][name*="_in[]"]');
      console.log('DEBUG: Found hidden multi-select inputs:', hiddenMultiSelectInputs.length);
      hiddenMultiSelectInputs.forEach(input => {
        console.log('DEBUG: Hidden multi-select input:', input.name, input.value);
        const name = input.name.replace('watch_list_filters[', '').replace('][]', '');
        if (input.value && input.value.trim() !== '') {
          if (filters[name]) {
            if (Array.isArray(filters[name])) {
              filters[name].push(input.value);
              console.log('DEBUG: Added to existing array:', name, filters[name]);
            } else {
              filters[name] = [filters[name], input.value];
              console.log('DEBUG: Converted to array:', name, filters[name]);
            }
          } else {
            filters[name] = [input.value];
            console.log('DEBUG: Created new array:', name, filters[name]);
          }
        }
      });
      console.log('DEBUG: After hidden multi-select collection:', filters);

      // Then, collect from main inputs (as fallback) - only if no hidden values exist
      const multiSelectInputs = editMode.querySelectorAll('input[data-main-input="true"][name*="watch_list_filters"]');
      console.log('DEBUG: Found multi-select inputs:', multiSelectInputs.length);
      multiSelectInputs.forEach(input => {
        console.log('DEBUG: Multi-select input:', input.name, input.value);
        const name = input.name.replace('watch_list_filters[', '').replace('][]', '');
        if (input.value && input.value.trim() !== '') {
          // Split comma-separated values back into array
          const values = input.value.split(',').filter(v => v.trim() !== '');
          if (values.length > 0) {
            // Only add if we don't already have values from hidden inputs
            if (!filters[name] || filters[name].length === 0) {
              filters[name] = values;
            } else {
              // Merge with existing values and remove duplicates
              filters[name] = [...new Set([...filters[name], ...values])];
            }
          }
        }
      });
      console.log('DEBUG: After main multi-select collection:', filters);

      // Collect range values (min/max inputs)
      const rangeInputs = editMode.querySelectorAll('input[type="number"][name*="watch_list_filters"]');
      console.log('DEBUG: Found range inputs:', rangeInputs.length);
      rangeInputs.forEach(input => {
        console.log('DEBUG: Range input:', input.name, input.value);
        const name = input.name.replace('watch_list_filters[', '').replace(']', '');
        if (input.value && input.value.trim() !== '') {
          filters[name] = input.value;
        }
      });

      // Collect date range values
      const dateInputs = editMode.querySelectorAll('input[type="date"][name*="watch_list_filters"]');
      console.log('DEBUG: Found date inputs:', dateInputs.length);
      dateInputs.forEach(input => {
        console.log('DEBUG: Date input:', input.name, input.value);
        const name = input.name.replace('watch_list_filters[', '').replace(']', '');
        if (input.value && input.value.trim() !== '') {
          filters[name] = input.value;
        }
      });

      // Collect toggle switch values (hidden inputs created by toggle switches)
      // Skip multi-select hidden inputs as they're already collected above
      const hiddenInputs = editMode.querySelectorAll('input[type="hidden"][name*="watch_list_filters"]:not([name$="_in[]"])');
      console.log('DEBUG: Found hidden inputs (non-multi-select):', hiddenInputs.length);
      hiddenInputs.forEach(input => {
        console.log('DEBUG: Hidden input:', input.name, input.value);
        const name = input.name.replace('watch_list_filters[', '').replace(']', '');
        if (input.value && input.value.trim() !== '') {
          // Handle single values (toggle switches, etc.)
          filters[name] = input.value;
        }
      });

      console.log('DEBUG: Collected filters:', filters);
      return filters;
    }

    handleWatchListFilterToggleSwitchChange(event) {
      const checkbox = event.target;
      const field = checkbox.getAttribute('data-field');
      const hiddenInput = checkbox.closest('.watch-list-edit-mode').querySelector(`input[name="watch_list_filters[${field}_eq]"]`);

      if (checkbox.checked) {
        if (!hiddenInput) {
          const newHiddenInput = document.createElement('input');
          newHiddenInput.type = 'hidden';
          newHiddenInput.name = `watch_list_filters[${field}_eq]`;
          newHiddenInput.value = '1';
          newHiddenInput.setAttribute('data-field', field);
          checkbox.closest('.watch-list-edit-mode').appendChild(newHiddenInput);
        }
      } else {
        if (hiddenInput) {
          hiddenInput.remove();
        }
      }
    }

    cancelWatchListInlineEdit(event) {
      const input = event.target;
      const watchListId = input.getAttribute('data-watch-list-id');

      // Find the display and edit mode elements
      const displayMode = document.querySelector(`.watch-list-display-mode[data-watch-list-id="${watchListId}"]`);
      const editMode = document.querySelector(`.watch-list-edit-mode[data-watch-list-id="${watchListId}"]`);

      if (displayMode && editMode) {
        // Reset input value to original name
        const originalName = displayMode.querySelector('.watch-list-name').getAttribute('data-watch-list-name');
        input.value = originalName;

        // Hide edit mode and show display mode
        displayMode.classList.remove('hidden');
        editMode.classList.add('hidden');
      }
    }

    deleteWatchList(event) {
      event.preventDefault();

      const watchListId = event.currentTarget.getAttribute('data-watch-list-id');
      const watchListName = event.currentTarget.getAttribute('data-watch-list-name');

      if (!confirm(`Are you sure you want to delete the watch list "${watchListName}"? This action cannot be undone.`)) {
        return;
      }

      fetch(`/watch_lists/${watchListId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        }
      })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // For 204 No Content responses, don't try to parse JSON
        if (response.status === 204) {
          return { success: true };
        }

        // For other successful responses, try to parse JSON
        return response.json();
      })
      .then(data => {
        if (data.success) {
          // Mark that changes have been made
          this.watchListChangesMade = true;

          // Remove the watch list item from the DOM
          if (event && event.currentTarget) {
            const watchListItem = event.currentTarget.closest('[data-sortable-item]');
            if (watchListItem) {
              watchListItem.remove();
            }
          } else {
            // Fallback: remove by watch list ID if event is not available
            const watchListItem = document.querySelector(`[data-sortable-item][data-watch-list-id="${watchListId}"]`);
            if (watchListItem) {
              watchListItem.remove();
            }
          }
        } else {
          throw new Error(data.errors ? data.errors.join(', ') : 'Failed to delete watch list');
        }
      })
      .catch(error => {
        alert(`Error deleting watch list: ${error.message}`);
      });
    }

  saveInlineEdit(event) {
    const input = event.target;
    const securityListId = input.getAttribute('data-security-list-id');
    const newName = input.value.trim();

    if (newName === '') {
      this.cancelInlineEdit(event);
      return;
    }

    // Send update request
    fetch(`/security_lists/${securityListId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      },
      body: JSON.stringify({ name: newName })
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      if (data.success) {
        // Update the security list name in display mode
        const displayMode = document.querySelector(`.security-list-display-mode[data-security-list-id="${securityListId}"]`);
        const nameSpan = displayMode.querySelector('.security-list-name');
        if (nameSpan) {
          nameSpan.textContent = newName;
          nameSpan.setAttribute('data-security-list-name', newName);
        }

        // Update the edit button's data attribute
        const editButton = document.querySelector(`[data-action*="startInlineEdit"][data-security-list-id="${securityListId}"]`);
        if (editButton) {
          editButton.setAttribute('data-security-list-name', newName);
        }

        // Mark that changes were made
        this.securityListChangesMade = true;

        // Also set modal data attribute
        const modal = document.getElementById("security-list-management-modal");
        if (modal) {
          modal.dataset.changesMade = 'true';
        }

        // Hide edit mode and show display mode
        this.cancelInlineEdit(event);
      } else {
        throw new Error(data.errors ? data.errors.join(', ') : 'Failed to update security list');
      }
    })
    .catch(error => {
      alert(`Error updating security list: ${error.message}`);
      this.cancelInlineEdit(event);
    });
  }

  cancelInlineEdit(event) {
    const input = event.target;
    const securityListId = input.getAttribute('data-security-list-id');

    // Find the display and edit mode elements
    const displayMode = document.querySelector(`.security-list-display-mode[data-security-list-id="${securityListId}"]`);
    const editMode = document.querySelector(`.security-list-edit-mode[data-security-list-id="${securityListId}"]`);

    if (displayMode && editMode) {
      // Reset input value to original name
      const originalName = displayMode.querySelector('.security-list-name').getAttribute('data-security-list-name');
      input.value = originalName;

      // Hide edit mode and show display mode
      displayMode.classList.remove('hidden');
      editMode.classList.add('hidden');
    }
  }

  deleteSecurityList(event) {
    event.preventDefault();

    const securityListId = event.currentTarget.getAttribute('data-security-list-id');
    const securityListName = event.currentTarget.getAttribute('data-security-list-name');

    if (!confirm(`Are you sure you want to delete the security list "${securityListName}"? This action cannot be undone.`)) {
      return;
    }

    fetch(`/security_lists/${securityListId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // For 204 No Content responses, don't try to parse JSON
      if (response.status === 204) {
        return { success: true };
      }

      // For other successful responses, try to parse JSON
      return response.json();
    })
    .then(data => {
      if (data.success) {
        // Remove the security list item from the DOM
        if (event && event.currentTarget) {
          const securityListItem = event.currentTarget.closest('[data-sortable-item]');
          if (securityListItem) {
            securityListItem.remove();
          }
        } else {
          // Fallback: remove by security list ID if event is not available
          const securityListItem = document.querySelector(`[data-sortable-item][data-security-list-id="${securityListId}"]`);
          if (securityListItem) {
            securityListItem.remove();
          }
        }

        // Mark that changes were made
        this.securityListChangesMade = true;

        // Also set modal data attribute
        const modal = document.getElementById("security-list-management-modal");
        if (modal) {
          modal.dataset.changesMade = 'true';
        }
      } else {
        throw new Error(data.errors ? data.errors.join(', ') : 'Failed to delete security list');
      }
    })
    .catch(error => {
      alert(`Error deleting security list: ${error.message}`);
    });
  }

  initializeDragAndDrop() {
    const container = document.getElementById('security-lists-list');
    if (!container) return;

    // Clean up any existing Sortable instance
    if (container._sortable) {
      container._sortable.destroy();
    }

    // Initialize Sortable.js
    container._sortable = new Sortable(container, {
      animation: 150,
      handle: '[data-sortable-handle]',
      ghostClass: 'sortable-ghost',
      chosenClass: 'sortable-chosen',
      dragClass: 'sortable-drag',
      onEnd: (evt) => {
        const oldIndex = evt.oldIndex;
        const newIndex = evt.newIndex;

        if (oldIndex !== newIndex) {
          this.reorderSecurityLists(oldIndex, newIndex);
        }
      }
    });
  }



  reorderSecurityLists(oldIndex, newIndex) {
    const container = document.querySelector('#security-lists-list');
    if (!container) return;

    // Update positions for all items (Sortable.js already handled the visual reordering)
    const newPositions = Array.from(container.children).map((item, index) => ({
      id: item.getAttribute('data-security-list-id'),
      position: index + 1
    }));

    this.updateSecurityListPositions(newPositions);
  }

  updateSecurityListPositions(positions) {
    fetch('/security_lists/reorder', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      },
      body: JSON.stringify({ positions })
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      if (data.success) {
        this.securityListChangesMade = true;

        // Also set modal data attribute
        const modal = document.getElementById("security-list-management-modal");
        if (modal) {
          modal.dataset.changesMade = 'true';
        }
      } else {
        throw new Error(data.errors ? data.errors.join(', ') : 'Failed to update security list positions');
      }
    })
    .catch(error => {
      alert(`Error updating security list positions: ${error.message}`);
    });
  }

  openAddToSecurityListModal(event) {
    event.preventDefault();

    // Get selected securities directly from localStorage instead of matching against current view
    const selectedRowData = JSON.parse(localStorage.getItem("selectedRowData") || "[]");
    if (selectedRowData.length === 0) {
      alert("Please select at least one security to add to security list");
      return;
    }

    // Use the selectedRowData directly since it contains the full security information
    const selectedSecurities = selectedRowData.map(row => ({
      slug: row.slug || row.id,
      name: row.name,
      isin: row.isin,
      currency_code: row.currency_code || 'AUD',
      gross_price: row.gross_price || '0.00'
    }));

    if (selectedSecurities.length === 0) {
      alert("No valid securities found. Please refresh the page and try again.");
      return;
    }

    // Update modal content with selected securities
    this.updateAddToSecurityListModalContent(selectedSecurities);

    // Show the modal
    this.showAddToSecurityListModal();
  }

  updateAddToSecurityListModalContent(selectedSecurities) {
    const modal = document.getElementById("add-to-security-list-modal");
    if (!modal) {
      return;
    }

    // Update the count
    const countElement = modal.querySelector('[data-selected-count]');
    if (countElement) {
      countElement.textContent = selectedSecurities.length.toString();
    }

    // Update the preview container
    const previewContainer = modal.querySelector('[data-securities-preview]');
    if (previewContainer) {
      const previewHtml = selectedSecurities.map(security => `
        <div class="flex items-center justify-between p-2 border-b border-neutral-200 last:border-b-0">
          <div class="flex-1 min-w-0">
            <div class="text-sm font-medium text-neutral-900 truncate">${security.name}</div>
            <div class="text-xs text-neutral-500">${security.isin}</div>
          </div>
          <div class="text-xs text-neutral-500 ml-2">
            ${security.currency_code} ${security.gross_price}
          </div>
        </div>
      `).join('');

      previewContainer.innerHTML = previewHtml || `
        <div class="text-center py-4 text-neutral-500">
          <p>No securities selected</p>
        </div>
      `;
    }

    // Store selected securities data in modal dataset
    modal.dataset.selectedSecurities = JSON.stringify(selectedSecurities);
  }

  showAddToSecurityListModal() {
    const modal = document.getElementById("add-to-security-list-modal");
    if (!modal) {
      return;
    }

    modal.classList.remove("hidden");
    modal.setAttribute("aria-hidden", "false");
  }

  closeAddToSecurityListModal() {
    const modal = document.getElementById("add-to-security-list-modal");
    if (modal) {
      modal.classList.add("hidden");
      modal.setAttribute("aria-hidden", "true");

      // Comprehensive state clearing
      this.resetAddToSecurityListModalState();
    }
  }

  resetAddToSecurityListModalState() {
    const modal = document.getElementById("add-to-security-list-modal");
    if (!modal) return;

    // Clear any stored data
    try {
      delete modal.dataset.selectedSecurities;
    } catch (modalDataError) {
      // Silent fail
    }

    // Reset the modal content to default state
    try {
      const countElement = modal.querySelector('[data-selected-count]');
      if (countElement) {
        countElement.textContent = '0';
      }

      const previewContainer = modal.querySelector('[data-securities-preview]');
      if (previewContainer) {
        previewContainer.innerHTML = `
          <div class="text-center py-4 text-neutral-500">
            <p>Select securities from the table to add to security lists</p>
          </div>
        `;
      }
    } catch (contentError) {
      // Silent fail
    }

    // Uncheck all checkboxes
    try {
      const checkboxes = modal.querySelectorAll('input[name="security_list_ids[]"]');
      checkboxes.forEach(checkbox => {
        checkbox.checked = false;
      });
    } catch (checkboxError) {
      // Silent fail
    }

    // Clear any other potential storage
    try {
      sessionStorage.removeItem('selectedSecurities');
      sessionStorage.removeItem('selectedRows');
    } catch (storageError) {
      // Silent fail
    }
  }

  addSecuritiesToSecurityLists() {
    const now = Date.now();
    const timeSinceLastCall = now - this.constructor.lastAddSecuritiesCall;

    // Prevent multiple calls within 2 seconds (global across all instances)
    if (timeSinceLastCall < 2000) {
      return;
    }

    // Prevent multiple simultaneous calls (global across all instances)
    if (this.constructor.isAddingSecurities) {
      return;
    }

    this.constructor.lastAddSecuritiesCall = now;
    this.constructor.isAddingSecurities = true;

    const modal = document.getElementById('add-to-security-list-modal');
    if (!modal) {
      alert('Modal not found. Please refresh the page and try again.');
      this.constructor.isAddingSecurities = false;
      return;
    }

    const selectedSecuritiesData = modal.dataset.selectedSecurities;
    if (!selectedSecuritiesData) {
      alert('No securities selected. Please select securities from the table first.');
      this.constructor.isAddingSecurities = false;
      return;
    }

    const selectedSecurities = JSON.parse(selectedSecuritiesData);
    if (!selectedSecurities || selectedSecurities.length === 0) {
      alert('No securities selected. Please select securities from the table first.');
      this.constructor.isAddingSecurities = false;
      return;
    }

    const selectedSecurityListIds = Array.from(modal.querySelectorAll('input[name="security_list_ids[]"]:checked'))
      .map(checkbox => checkbox.value);

    if (selectedSecurityListIds.length === 0) {
      alert('Please select at least one security list to add securities to.');
      this.constructor.isAddingSecurities = false;
      return;
    }

    const isins = selectedSecurities.map(security => security.slug);

    fetch('/security_lists/add_securities', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      },
      body: JSON.stringify({
        security_list_ids: selectedSecurityListIds,
        isins: isins
      })
    })
    .then(async response => {
      if (!response.ok) {
        const data = await response.json();
        if (data.errors) {
          throw new Error(`${data.errors.join(', ')}`);
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

      }
      return response.json();
    })
    .then(data => {
      if (data.success) {
        try {
          // Temporarily disable grid selection callback to prevent localStorage from being set back
          let originalOnSelectedRowsChanged = null;
          if (this.grid && this.grid.onSelectedRowsChanged) {
            originalOnSelectedRowsChanged = this.grid.onSelectedRowsChanged;
            this.grid.onSelectedRowsChanged = null;
          }

          // Clear selected rows from localStorage
          localStorage.removeItem("selectedRows");
          localStorage.setItem("selectedRows", "[]");
          localStorage.removeItem("selectedRowData");
          localStorage.setItem("selectedRowData", "[]");

          // Clear grid selection if grid exists
          if (this.grid) {
            try {
              // Temporarily disable all grid event handlers to prevent notify errors
              const originalEventHandlers = {};
              if (this.grid.onSelectedRowsChanged) {
                originalEventHandlers.onSelectedRowsChanged = this.grid.onSelectedRowsChanged;
                this.grid.onSelectedRowsChanged = null;
              }
              if (this.grid.onSelectedRowIdsChanged) {
                originalEventHandlers.onSelectedRowIdsChanged = this.grid.onSelectedRowIdsChanged;
                this.grid.onSelectedRowIdsChanged = null;
              }
              if (this.grid.onSelectedRangesChanged) {
                originalEventHandlers.onSelectedRangesChanged = this.grid.onSelectedRangesChanged;
                this.grid.onSelectedRangesChanged = null;
              }

              // Clear the selection
              this.grid.setSelectedRows([]);

              // Re-enable event handlers
              if (originalEventHandlers.onSelectedRowsChanged) {
                this.grid.onSelectedRowsChanged = originalEventHandlers.onSelectedRowsChanged;
              }
              if (originalEventHandlers.onSelectedRowIdsChanged) {
                this.grid.onSelectedRowIdsChanged = originalEventHandlers.onSelectedRowIdsChanged;
              }
              if (originalEventHandlers.onSelectedRangesChanged) {
                this.grid.onSelectedRangesChanged = originalEventHandlers.onSelectedRangesChanged;
              }
            } catch (gridError) {
              // Continue with other operations even if grid clearing fails
            }
          }

          // Clear modal data
          try {
            delete modal.dataset.selectedSecurities;
          } catch (modalDataError) {
            // Silent fail
          }

          // Reset modal content
          try {
            const countElement = modal.querySelector('[data-selected-count]');
            if (countElement) {
              countElement.textContent = '0';
            }

            const previewContainer = modal.querySelector('[data-securities-preview]');
            if (previewContainer) {
              previewContainer.innerHTML = `
                <div class="text-center py-4 text-neutral-500">
                  <p>Select securities from the table to add to security lists</p>
                </div>
              `;
            }
          } catch (contentError) {
            // Silent fail
          }

          // Uncheck all checkboxes
          try {
            const checkboxes = modal.querySelectorAll('input[name="security_list_ids[]"]');
            checkboxes.forEach(checkbox => {
              checkbox.checked = false;
            });
          } catch (checkboxError) {
            // Silent fail
          }

          // Clear any other potential storage
          try {
            sessionStorage.removeItem('selectedSecurities');
            sessionStorage.removeItem('selectedRows');
          } catch (storageError) {
            // Silent fail
          }

          // Close the modal
          try {
            this.closeAddToSecurityListModal();
          } catch (closeError) {
            // Silent fail
          }

          // Re-enable grid selection callback if it was disabled
          if (originalOnSelectedRowsChanged) {
            this.grid.onSelectedRowsChanged = originalOnSelectedRowsChanged;
          }

          // Update the add/remove selected link state after clearing selections
          this.updateAddRemoveSelectedLinkState([]);

          // Dispatch custom event for selection changes
          document.dispatchEvent(new CustomEvent('securities-selection-changed', {
            detail: { selectedRows: [] }
          }));

          // Show success message and redirect or reload based on number of security lists
          alert(data.message);
          setTimeout(() => {
            if (selectedSecurityListIds.length === 1) {
              // Redirect to the specific security list if only one was selected
              window.location.href = `/securities?security_list=${selectedSecurityListIds[0]}`;
            } else {
              // Reload page if multiple security lists were selected
              window.location.reload();
            }
          }, 100);
        } catch (error) {
          // Still show success message and reload even if there's an error
          alert(data.message);
          window.location.reload();
        }
      } else {
        throw new Error(data.errors ? data.errors.join(', ') : 'Failed to add securities to security lists');
      }
    })
    .catch(error => {
      let errorMessage = 'Failed to add securities to security lists';

      if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      alert(errorMessage);
    })
    .finally(() => {
      // Always reset the flag when the operation completes (global across all instances)
      this.constructor.isAddingSecurities = false;
    });
  }

  removeSecuritiesFromSecurityList(event) {
    event.preventDefault();

    const selectedRows = JSON.parse(localStorage.getItem("selectedRows") || "[]");
    if (selectedRows.length === 0) {
      alert("Please select at least one security to remove from security list");
      return;
    }

    // Get current security list ID from URL params
    const urlParams = new URLSearchParams(window.location.search);
    const securityListId = urlParams.get('security_list');

    if (!securityListId || securityListId.trim() === '') {
      alert("No security list selected");
      return;
    }

    if (!confirm("Are you sure you want to remove the selected securities from this security list?")) {
      return;
    }

    const isins = selectedRows;

    fetch('/security_lists/remove_securities', {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      },
      body: JSON.stringify({
        security_list_id: securityListId,
        isins: isins
      })
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      if (data.success) {
        try {
          // Clear selected rows from localStorage
          localStorage.removeItem("selectedRows");
          localStorage.setItem("selectedRows", "[]");
          localStorage.removeItem("selectedRowData");
          localStorage.setItem("selectedRowData", "[]");

          // Clear grid selection if grid exists
          if (this.grid) {
            try {
              // Use a safer approach to clear selection without disabling handlers
              // First, try to clear using the selection model directly
              if (this.grid.getSelectionModel()) {
                this.grid.getSelectionModel().setSelectedRanges([]);
              } else {
                // Fallback: try setSelectedRows with error handling
                try {
                  this.grid.setSelectedRows([]);
                } catch (setSelectedRowsError) {
                  // Alternative: try to clear by setting each selected row individually
                  const currentSelectedRows = this.grid.getSelectedRows();
                  if (currentSelectedRows && currentSelectedRows.length > 0) {
                    currentSelectedRows.forEach(rowIndex => {
                      try {
                        this.grid.setSelectedRows([rowIndex], false);
                      } catch (individualError) {
                        // Silent fail
                      }
                    });
                  }
                }
              }
            } catch (gridError) {
              // Continue with other operations even if grid clearing fails
            }
          }

          // Clear any other potential storage
          try {
            sessionStorage.removeItem('selectedSecurities');
            sessionStorage.removeItem('selectedRows');
          } catch (storageError) {
            // Silent fail
          }

          // Update the add/remove selected link state after clearing selections
          this.updateAddRemoveSelectedLinkState([]);

          // Dispatch custom event for selection changes
          document.dispatchEvent(new CustomEvent('securities-selection-changed', {
            detail: { selectedRows: [] }
          }));

          setTimeout(() => {
            // Redirect back to the security list page
            window.location.href = `/securities?security_list=${securityListId}`;
          }, 100);
        } catch (error) {
          // Still show success message and redirect even if there's an error
          alert(data.message);
          window.location.href = `/securities?security_list=${securityListId}`;
        }
      } else {
        throw new Error(data.errors ? data.errors.join(', ') : 'Failed to remove securities from security list');
      }
    })
    .catch(error => {
      alert(`Error removing securities from security list: ${error.message}`);
    });
  }

  clearMultiSelectDisplay(component) {
    const selectedTextSpan = component.querySelector('[data-uikit--select-multi-target="selectedText"]');
    const hiddenInput = component.querySelector('[data-uikit--select-multi-target="hiddenInput"]');

    if (selectedTextSpan) {
      // Find the placeholder text from the original placeholder span
      const placeholderSpan = selectedTextSpan.querySelector('span.text-neutral-500');
      const placeholderText = placeholderSpan ? placeholderSpan.textContent : 'Select...';

      selectedTextSpan.innerHTML = `<span class="text-neutral-500">${placeholderText}</span>`;
    }

    if (hiddenInput) {
      hiddenInput.value = '';
    }

    // Clear all checkboxes in the dropdown
    const checkboxes = component.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach(checkbox => {
      checkbox.checked = false;
    });
  }

  toggleInventoryOnly(event) {
    event.preventDefault();
    const currentState = document.cookie.includes('inventory_only=true');
    const newState = !currentState;
    document.cookie = `inventory_only=${newState}; path=/`;
    this.submitForm();
  }

  createQuoteWithSelected(event) {
    event.preventDefault();

    const selectedRows = JSON.parse(localStorage.getItem("selectedRows") || "[]");
    if (selectedRows.length === 0) {
      window.location.href = `/oms/quotes/new`;
    } else {
      const isinsParam = selectedRows.join(',').toUpperCase();
      window.location.href = `/oms/quotes/new?isins=${isinsParam}`;
    }
  }

  createSecurityList(event) {
    event.preventDefault();

    // Get the ISINs from local storage
    let savedSelectedRows = JSON.parse(localStorage.getItem("selectedRows") || "[]");

    // Show a simple prompt for now - could be enhanced with a modal
    var securityListName = prompt("Enter security list name:");

    if (securityListName === null) {
      return; // User cancelled the prompt
    }

    if (!securityListName || securityListName.trim() === "") {
      securityListName = "New Security List";
    }

    // Extract ISINs from the selected rows
    const isins = JSON.parse(localStorage.getItem("selectedRows") || "[]");

    if (isins.length === 0) {
      alert("Please select at least one security to add to the security list.");
      return;
    }

    // Get CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

    // Send JSON request to create security list
    fetch('/security_lists', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({
        security_list: {
          name: securityListName,
          isins: isins
        }
      })
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        // Clear localStorage on successful creation
        localStorage.removeItem("selectedRows");

        // Dispatch custom event for selection changes
        document.dispatchEvent(new CustomEvent('securities-selection-changed', {
          detail: { selectedRows: [] }
        }));

        // Redirect to securities page with security list parameter
        window.location.href = `/securities?security_list=${data.security_list_id}`;
      } else {
        // Show error message
        alert(`Error creating security list: ${data.errors}`);
      }
    })
    .catch(error => {
      console.error('Error creating security list:', error);
      alert('Error creating security list. Please try again.');
    });
  }

  createWatchlist(event) {
    event.preventDefault();

    // Check if user managed watch lists are enabled
    if (!this.userManagedWatchlistsEnabledValue) {
      alert("Watch list feature is not available.");
      return;
    }

    // Show a simple prompt for watch list name
    var watchListName = prompt("Enter watch list name:");

    if (watchListName === null) {
      return; // User cancelled the prompt
    }

    if (!watchListName || watchListName.trim() === "") {
      watchListName = "New Watch List";
    }

    // Get current filter parameters from URL or form
    const urlParams = new URLSearchParams(window.location.search);
    const filters = {};

    // First try to get filters from URL parameters
    // Handle multiple values for the same parameter
    for (const [key, value] of urlParams.entries()) {
      if (key.startsWith('q[') && value) {
        if (filters[key]) {
          // If key already exists, convert to array and add new value
          if (Array.isArray(filters[key])) {
            filters[key].push(value);
          } else {
            filters[key] = [filters[key], value];
          }
        } else {
          filters[key] = value;
        }
      }
    }

    // If no URL filters, try to get from form
    if (Object.keys(filters).length === 0) {
      const form = document.getElementById('securities-form');
      if (form) {
        const formData = new FormData(form);
        for (let [key, value] of formData.entries()) {
          if (key.startsWith('q[') && value) {
            filters[key] = value;
          }
        }
      }
    }

    // Check if any filters are present
    if (Object.keys(filters).length === 0) {
      alert("Please apply some filters before creating a watch list.");
      return;
    }

    // Get CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

    // Send JSON request to create watch list
    fetch('/watch_lists', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({
        watch_list: {
          name: watchListName,
          filters: filters
        }
      })
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        // Redirect to securities page with the new watch list ID
        window.location.href = `/securities?watch_list=${data.watch_list_id}`;
      } else {
        // Show error message
        alert(`Error creating watch list: ${data.errors}`);
      }
    })
    .catch(error => {
      console.error('Error creating watch list:', error);
      alert('Error creating watch list. Please try again.');
    });
  }

  reinitializeUikitComponents(editMode) {
    console.log('DEBUG: Reinitializing Uikit components in edit mode');

    // Force update of all form inputs to reflect their current values
    const allInputs = editMode.querySelectorAll('input, select');
    allInputs.forEach(input => {
      // Trigger change event to update any dependent components
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    // Reinitialize SelectMulti components
    const selectMultiComponents = editMode.querySelectorAll('[data-controller*="uikit--select-multi"]');
    selectMultiComponents.forEach(component => {
      console.log('DEBUG: Reinitializing SelectMulti component');
      // Find the hidden input and update its value display
      const hiddenInput = component.querySelector('input[data-main-input="true"]');
      const selectedText = component.querySelector('[data-uikit--select-multi-target="selectedText"]');
      const button = component.querySelector('[data-uikit--select-multi-target="button"]');

      console.log('DEBUG: Hidden input value:', hiddenInput?.value);
      console.log('DEBUG: Selected text element:', selectedText);

      if (hiddenInput && selectedText) {
        const values = hiddenInput.value ? hiddenInput.value.split(',').filter(v => v.trim() !== '') : [];
        console.log('DEBUG: Parsed values:', values);

        if (values.length > 0) {
          // Map IDs to names by looking up the option elements
          const optionElements = component.querySelectorAll('[data-uikit--select-multi-target="option"]');
          const mappedNames = values.map(id => {
            const option = Array.from(optionElements).find(opt => opt.dataset.value === id);
            return option ? option.dataset.label : id; // Fallback to ID if name not found
          });

          if (mappedNames.length <= 2) {
            selectedText.textContent = mappedNames.join(', ');
          } else {
            selectedText.textContent = `${mappedNames[0]} +${mappedNames.length - 1} more`;
          }
        } else {
          selectedText.textContent = 'Select options...';
        }

        // Also update the button text
        if (button) {
          button.querySelector('[data-uikit--select-multi-target="selectedText"]').textContent = selectedText.textContent;
        }
      }
    });

    // Reinitialize RangeDecimal components
    const rangeDecimalComponents = editMode.querySelectorAll('[data-controller*="uikit--range-decimal"]');
    rangeDecimalComponents.forEach(component => {
      console.log('DEBUG: Reinitializing RangeDecimal component');
      const minInput = component.querySelector('input[type="number"]:first-of-type');
      const maxInput = component.querySelector('input[type="number"]:last-of-type');
      console.log('DEBUG: RangeDecimal min value:', minInput?.value);
      console.log('DEBUG: RangeDecimal max value:', maxInput?.value);
    });

    // Reinitialize RangeDate components
    const rangeDateComponents = editMode.querySelectorAll('[data-controller*="uikit--range-date"]');
    rangeDateComponents.forEach(component => {
      console.log('DEBUG: Reinitializing RangeDate component');
      const minInput = component.querySelector('input[type="date"]:first-of-type');
      const maxInput = component.querySelector('input[type="date"]:last-of-type');
      console.log('DEBUG: RangeDate min value:', minInput?.value);
      console.log('DEBUG: RangeDate max value:', maxInput?.value);
    });

    // Reinitialize ToggleSwitch components
    const toggleSwitchComponents = editMode.querySelectorAll('[data-controller*="uikit--toggle-switch"]');
    toggleSwitchComponents.forEach(component => {
      console.log('DEBUG: Reinitializing ToggleSwitch component');
      const checkbox = component.querySelector('input[type="checkbox"]');
      const hiddenInput = component.querySelector('input[type="hidden"]');
      console.log('DEBUG: ToggleSwitch checked:', checkbox?.checked);
      console.log('DEBUG: ToggleSwitch hidden value:', hiddenInput?.value);
    });

    // Force a small delay to ensure components are updated
    setTimeout(() => {
      console.log('DEBUG: Uikit components reinitialization complete');
    }, 100);
  }
}
